import React, { useEffect, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAppMode } from '../contexts/AppModeContext';
import { usePendingApprovals } from '../contexts/PendingApprovalsContext';
import AdminSubBar, { ADMIN_SUBBAR_HEIGHT, useAdminLocalBar } from './AdminSubBar';
import {
  Home,
  Upload,
  Users,
  User,
  Database,
  CheckCircle,
} from 'lucide-react';
import Header, { HEADER_HEIGHT } from './Header/Header';
import AgentNav from './agent/AgentNav';
import AgentModeHost from './agent/AgentModeHost';
import { AgentSettings } from './agent/AgentSettings';
import { formatSessionId, getOrCreateSessionNumber, getOwnerId } from '../lib/supabase';
import { VIEW_ROUTES } from '../lib/view-keys';

function pathToView(pathname) {
  if (pathname.startsWith('/upload') || pathname.startsWith('/create-artist')) return 'data';
  if (pathname.startsWith('/approvals') || pathname.startsWith('/master-tracks')) return 'review';
  if (pathname.startsWith('/profile')) return 'settings';
  return 'dashboard';
}

const Layout = () => {
  const { user, logout } = useAuth();
  const { isAgent, setAppMode } = useAppMode();
  const location = useLocation();
  const navigate = useNavigate();
  const { pendingCount } = usePendingApprovals();
  const showAdminBar = useAdminLocalBar(user?.role === 'admin');
  const showModeBar = Boolean(user);
  const [agentView, setAgentView] = useState(() => pathToView(location.pathname));
  const [showAgentSettings, setShowAgentSettings] = useState(false);
  const [agentTimerActive, setAgentTimerActive] = useState(false);
  const [sessionLabel, setSessionLabel] = useState('MB_HUM_SESSION_ID_------');

  useEffect(() => {
    setAgentView(pathToView(location.pathname));
  }, [location.pathname]);

  useEffect(() => {
    if (!isAgent) {
      setShowAgentSettings(false);
      setAgentTimerActive(false);
    }
  }, [isAgent]);

  useEffect(() => {
    let cancelled = false;
    const ownerId = getOwnerId();
    void getOrCreateSessionNumber(ownerId)
      .then((n) => {
        if (!cancelled) setSessionLabel(formatSessionId(n));
      })
      .catch(() => {
        if (!cancelled) setSessionLabel(formatSessionId(null));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const navigation = [
    { name: 'Home', href: '/dashboard', icon: Home },
    { name: 'Data Entry', href: '/upload', icon: Upload },
    { name: 'Add Artist', href: '/create-artist', icon: User },
    ...(user?.role === 'admin'
      ? [
          { name: 'Track Library', href: '/master-tracks', icon: Database },
          { name: 'Artist Library', href: '/artist-list', icon: Users },
          {
            name: 'Review',
            href: '/approvals',
            icon: CheckCircle,
            badge: pendingCount > 0 ? pendingCount : null,
          },
        ]
      : []),
  ];

  const isFullBleed = [
    '/master-tracks',
    '/upload',
    '/artist-list',
    '/submissions',
    '/approvals',
    '/create-artist',
  ].includes(location.pathname);
  const chromeTop = HEADER_HEIGHT + (showModeBar ? ADMIN_SUBBAR_HEIGHT : 0);
  const showControlPlane = isAgent && agentView === 'dashboard';

  const handleAgentViewChange = (view) => {
    setAgentView(view);
    navigate(VIEW_ROUTES[view] || '/dashboard');
  };

  if (showAgentSettings) {
    return (
      <div className="app-screen min-h-screen text-white font-inter agent-mode">
        <AgentSettings onBack={() => setShowAgentSettings(false)} />
      </div>
    );
  }

  return (
    <div className={`app-screen min-h-screen text-white font-inter${isAgent ? ' agent-mode' : ''}`}>
      {isAgent ? (
        <AgentNav view={agentView} onViewChange={handleAgentViewChange} sessionLabel={sessionLabel} />
      ) : (
        <aside className="fixed top-0 left-0 bottom-0 z-40 w-60 border-r border-white/[0.08] bg-black/30 backdrop-blur-[30px]">
          <div className="flex h-full flex-col" style={{ paddingTop: chromeTop }}>
            <nav className="flex-1 pb-4 shrink-0 pt-6">
              <ul className="space-y-0">
                {navigation.map((item, index) => {
                  const isActive = location.pathname === item.href;
                  const Icon = item.icon;
                  return (
                    <li
                      key={item.name}
                      className="animate-[slideIn_1s_cubic-bezier(0.16,1,0.3,1)_both]"
                      style={{ animationDelay: `${0.4 + index * 0.12}s` }}
                    >
                      <Link
                        to={item.href}
                        className={`group w-full flex items-center gap-3 px-5 py-2.5 text-[0.75rem] font-medium tracking-normal transition-all duration-300 ${
                          isActive
                            ? 'bg-white/10 text-white'
                            : 'text-white/60 hover:text-white hover:bg-white/[0.06]'
                        }`}
                      >
                        <Icon
                          size={15}
                          className={`transition-colors duration-300 shrink-0 ${
                            isActive ? 'text-white' : 'text-white/50 group-hover:text-white/80'
                          }`}
                        />
                        <span className="flex-1 text-left whitespace-nowrap">{item.name}</span>
                        {item.badge && (
                          <span className="ml-auto inline-flex items-center justify-center px-1.5 py-0.5 text-[10px] font-bold leading-none text-white bg-red-500 rounded-full min-w-[18px]">
                            {item.badge}
                          </span>
                        )}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </nav>

            <div className="absolute bottom-24 left-0 right-0 px-5 py-2">
              <button
                type="button"
                onClick={() => logout()}
                className="group w-full flex items-center gap-3 px-5 py-2.5 text-[0.75rem] font-medium text-white/40 hover:text-white hover:bg-white/[0.06] transition-all duration-300"
              >
                <span className="flex-1 text-left whitespace-nowrap">Sign Out</span>
              </button>
            </div>
          </div>
        </aside>
      )}

      <Header
        onOpenAgentSettings={() => setShowAgentSettings(true)}
        agentTimerActive={agentTimerActive}
        onStartAgent={() => {
          setAppMode('agent');
          setAgentTimerActive(true);
        }}
        onStopAgent={() => setAgentTimerActive(false)}
      />

      {showModeBar && (
        <AdminSubBar isAgentMode={isAgent} showAdvancedTasks={showAdminBar} />
      )}

      <div className={isAgent ? 'pl-64' : 'pl-60'}>
        <main className={isFullBleed || showControlPlane ? 'pb-0' : 'pb-6'} style={{ paddingTop: chromeTop }}>
          {showControlPlane ? (
            <AgentModeHost onTimerStart={() => setAgentTimerActive(true)} />
          ) : isFullBleed ? (
            <Outlet />
          ) : (
            <div className="mx-auto px-4 sm:px-6 lg:px-8" style={{ padding: '40px' }}>
              <Outlet />
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default Layout;
